import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Grid, Paper } from '@mui/material';
import { TraceHistory } from 'types';
import { addHours } from 'date-fns';
import { getTraceHistory } from 'api/snapshotApi';
import useInterval from 'hooks/useInterval';
import AlarmWidget from 'features/alarmWidget/AlarmWidget';
import Spectrogram from 'features/spectrogram/Spectrogram';
import TraceHistoryPlayBack from 'features/traceHistory/TraceHistoryPlayback';
import { useAPICall } from 'hooks/useAPICall';
import { CenteredCircularProgress } from 'components/CenteredCircularProgress';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';
import { zonedTimeToUtc } from 'date-fns-tz';
import SpectrumChart from 'features/spectrumChart/SpectrumChart';

const DetailedSnapshotView = () => {
  const params = useParams();
  const navigate = useNavigate();
  const wallId = Number(params.wallId);
  const snapshotId = Number(params.snapshotId);
  const analyzerId = Number(params.analyzerId);
  const { timezone, getCurrentDateTime } = useUserDateTime();

  const [traceHistory, setTraceHistory] = useState<TraceHistory[]>([]);
  const [traceIndex, setTraceIndex] = useState<number>(0);

  const handleTraceIndexChanged = (newValue: any) => {
    setTraceIndex(newValue);
  };

  const getHistory = useCallback(async () => {
    const twoHoursAgo = zonedTimeToUtc(
      addHours(getCurrentDateTime(), -2),
      timezone
    ).toISOString();
    const currentDateTime = zonedTimeToUtc(
      getCurrentDateTime(),
      timezone
    ).toISOString();

    const response = await getTraceHistory(
      snapshotId,
      twoHoursAgo,
      currentDateTime
    );
    setTraceHistory(response);
  }, [snapshotId, timezone, getCurrentDateTime]);

  const { safeCall: safeGetHistory, isLoading } = useAPICall(getHistory);

  const handleSingleTraceViewClicked = () => {
    navigate(`/wall/${wallId}`);
  };

  const handleHistoryClicked = () => {
    const twoHoursAgo = zonedTimeToUtc(
      addHours(getCurrentDateTime(), -2),
      timezone
    ).toISOString();
    const currentDateTime = zonedTimeToUtc(
      getCurrentDateTime(),
      timezone
    ).toISOString();

    navigate(
      `/trace-history?start=${twoHoursAgo}&end=${currentDateTime}&analyzer=${analyzerId}&snapshot=${snapshotId}&trace=0`
    );
  };

  useEffect(() => {
    safeGetHistory();
  }, [safeGetHistory]);

  useInterval(() => {
    safeGetHistory();
  }, 60000);

  return (
    <Grid container>
      <Grid
        component={Paper}
        item
        xs={6}
        onClick={handleSingleTraceViewClicked}
      >
        <SpectrumChart analyzerId={analyzerId} snapshotId={snapshotId} />
      </Grid>
      <Grid component={Paper} item xs={6}>
        {traceHistory.length > 0 && (
          <TraceHistoryPlayBack
            filter={{
              analyzerId: analyzerId,
              snapshotId: snapshotId,
            }}
            traceHistory={traceHistory}
            traceCount={traceHistory.length}
            handleTraceIndexChanged={handleTraceIndexChanged}
            traceIndex={traceIndex}
            handleChartClicked={handleHistoryClicked}
          />
        )}
        {isLoading ? (
          <CenteredCircularProgress />
        ) : (
          traceHistory.length === 0 && <h4>No History to Display</h4>
        )}
      </Grid>
      <Grid component={Paper} item xs={6}>
        <Spectrogram
          analyzerId={analyzerId}
          snapshotId={snapshotId}
          hideLabels
        />
      </Grid>
      <Grid item xs={6}>
        <AlarmWidget
          analyzerIdFilter={analyzerId}
          snapshotIdFilter={snapshotId}
        />
      </Grid>
    </Grid>
  );
};

export default DetailedSnapshotView;
